import * as Sentry from '@sentry/react';
import { isChunkLoadError } from './lazyWithRetry';

/**
 * Sentry (project krp-frontend) — no-op ถ้าไม่ตั้ง VITE_SENTRY_DSN
 *
 * - environment จาก VITE_ENV (production/development) ให้ตรงกับ backend (krp-api)
 * - release = krp-frontend@<version>+<git sha> (ค่าเดียวกับที่โชว์มุมล่างซ้าย) ใช้ไล่ว่า error มากับ build ไหน
 * - ไม่ส่ง PII (ไม่มี IP/อีเมล) — ผูก user แค่ id/username/role ของพนักงานเพื่อให้ support ตามเคสได้
 * - chunk หายหลัง deploy ไม่ส่ง (ErrorBoundary reload ให้เองอยู่แล้ว — ไม่ใช่ bug)
 */
export function initSentry(): boolean {
  const dsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim();
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_ENV as string | undefined) || import.meta.env.MODE,
    release: `krp-frontend@${__APP_VERSION__}+${__GIT_SHA__}`,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    beforeSend(event, hint) {
      const err = hint?.originalException;
      if (err instanceof Error && isChunkLoadError(err)) return null;
      return event;
    },
  });
  return true;
}

type SentryUserLike = {
  id?: number | string;
  username?: string;
  role?: string | { name?: string } | null;
} | null;

export function setSentryUser(user: SentryUserLike): void {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  const role = typeof user.role === 'string' ? user.role : user.role?.name;
  Sentry.setUser({ id: user.id != null ? String(user.id) : undefined, username: user.username });
  Sentry.setTag('user.role', role || 'unknown');
}

export function captureBoundaryError(error: Error, componentStack?: string | null): void {
  Sentry.captureException(error, {
    contexts: componentStack ? { react: { componentStack } } : undefined,
    tags: { source: 'ErrorBoundary' },
  });
}
